/**
 * 表示フィルタ（シェーダ）。
 *
 * レトロ機は CRT で見ることを前提に絵が作られているので、
 * 素のドット表示だと意図より硬く・粗く見える。
 * ここでは「重すぎず、iPhone の GPU でも 60fps を割らない」範囲に絞っている。
 */

export type ShaderId = 'none' | 'scanlines' | 'crt' | 'lcd';

export interface ShaderInfo {
  id: ShaderId;
  name: string;
  description: string;
}

export const SHADERS: ShaderInfo[] = [
  { id: 'none', name: 'なし', description: '素のドット表示（最も軽い）' },
  { id: 'scanlines', name: 'スキャンライン', description: '走査線を重ねる。CRT らしさが出る' },
  { id: 'crt', name: 'CRT', description: '走査線 + 開口格子 + 軽い発光' },
  { id: 'lcd', name: 'LCD', description: 'ドット間に格子。GB の液晶らしさ' },
];

export const VERTEX_SHADER = `#version 100
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  // aPos は -1..1 のフルスクリーン三角形。UV は上下反転させる
  // （テクスチャは左上原点で流し込むため）。
  vUv = vec2((aPos.x + 1.0) * 0.5, (1.0 - aPos.y) * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const HEADER = `#version 100
precision mediump float;
varying vec2 vUv;
uniform sampler2D uTex;
/** テクスチャ（＝コアのネイティブ解像度）のサイズ */
uniform vec2 uTexSize;
/** 実際に描画されている画面のサイズ（ピクセル） */
uniform vec2 uOutSize;
`;

const NONE = `${HEADER}
void main() {
  gl_FragColor = texture2D(uTex, vUv);
}`;

/**
 * 走査線。
 * 出力の実解像度を基準に強度を決めるのが要点。テクスチャ解像度で
 * 引くと、拡大率が小さいとき縞が潰れてモアレになる。
 */
const SCANLINES = `${HEADER}
void main() {
  vec3 c = texture2D(uTex, vUv).rgb;

  float lines = uTexSize.y;
  float f = fract(vUv.y * lines);
  // 走査線の中心を明るく、境目を暗く
  float scan = 0.65 + 0.35 * sin(f * 3.14159265);

  // 拡大率が小さいときは効果を弱める（潰れて汚くなるため）
  float scale = uOutSize.y / uTexSize.y;
  float strength = clamp((scale - 1.0) / 2.0, 0.0, 1.0);
  scan = mix(1.0, scan, strength);

  gl_FragColor = vec4(c * scan, 1.0);
}`;

/**
 * CRT 風。走査線に加えて開口格子（RGB サブピクセル）と軽い発光を足す。
 * 曲面歪みは iPhone だと酔いやすく、実機の見た目とも遠いので入れていない。
 */
const CRT = `${HEADER}
void main() {
  vec3 c = texture2D(uTex, vUv).rgb;

  // --- 走査線 ---
  float f = fract(vUv.y * uTexSize.y);
  float scan = 0.6 + 0.4 * sin(f * 3.14159265);
  float scale = uOutSize.y / uTexSize.y;
  float strength = clamp((scale - 1.0) / 2.0, 0.0, 1.0);
  scan = mix(1.0, scan, strength);

  // --- 開口格子 ---
  // 出力ピクセル 3 個で 1 組。横方向に RGB を強調する。
  float px = vUv.x * uOutSize.x;
  float phase = mod(px, 3.0);
  vec3 mask = vec3(0.85);
  if (phase < 1.0)      mask = vec3(1.05, 0.85, 0.85);
  else if (phase < 2.0) mask = vec3(0.85, 1.05, 0.85);
  else                  mask = vec3(0.85, 0.85, 1.05);
  mask = mix(vec3(1.0), mask, strength);

  vec3 outc = c * scan * mask;

  // --- 発光 ---
  // 暗くなった分を明るい部分から補う。単純だが見た目の落ち込みが減る。
  float lum = dot(c, vec3(0.299, 0.587, 0.114));
  outc += c * lum * 0.15 * strength;

  gl_FragColor = vec4(outc, 1.0);
}`;

/**
 * LCD 風。ドット間に格子を入れる。GB / GBC に合う。
 */
const LCD = `${HEADER}
void main() {
  vec3 c = texture2D(uTex, vUv).rgb;

  vec2 f = fract(vUv * uTexSize);
  vec2 scale = uOutSize / uTexSize;
  // 1 ドットが 3px 未満だと格子で潰れるので効果を弱める
  float strength = clamp((min(scale.x, scale.y) - 2.0) / 3.0, 0.0, 1.0);

  // ドットの縁を暗くする
  float gx = smoothstep(0.0, 0.12, f.x) * smoothstep(0.0, 0.12, 1.0 - f.x);
  float gy = smoothstep(0.0, 0.12, f.y) * smoothstep(0.0, 0.12, 1.0 - f.y);
  float grid = mix(1.0, 0.78 + 0.22 * gx * gy, strength);

  gl_FragColor = vec4(c * grid, 1.0);
}`;

export function fragmentShaderFor(id: ShaderId): string {
  switch (id) {
    case 'scanlines':
      return SCANLINES;
    case 'crt':
      return CRT;
    case 'lcd':
      return LCD;
    case 'none':
      return NONE;
  }
}
